import React, { useState, useEffect } from 'react';
import { Form, Button, InputGroup, Modal } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { setCookie } from '../../utils/SecurityUtils';
import { marked } from 'marked';
import axios from 'axios';
import './RegisterForm.css';

function RegisterForm() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        companyName: '',
        password: '',
    });

    const [errors, setErrors] = useState([]);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [eulaAccepted, setEulaAccepted] = useState(false);
    const [agreements, setAgreements] = useState({
        eula: '',
        privacy: '',
    });
    const [showModal, setShowModal] = useState({ open: false, content: '', title: '' });
    const eulaID = 4;

    useEffect(() => {
        const fetchAgreements = async () => {
            try {
                const response = await axios.get(
                    `https://api.foveaai.com/evaluate/web/eula/${eulaID}`
                );
                const data = JSON.parse(response.data);
                setAgreements({
                    eula: marked(data.eula_content),
                    privacy: marked(data.privacy_content),
                });
            } catch (error) {
                console.error("Failed to fetch EULA", error);
            }
        };

        fetchAgreements();
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const toggleModal = (contentKey, title) => {
        setShowModal({
            open: !showModal.open,
            content: agreements[contentKey] || '',
            title: title,
        });
    };

    const validateForm = () => {
        const newErrors = [];
        if (!formData.email) newErrors.push({ message: "An <b>Email</b> address is required to create your account.", boldFields: ["Email"] });
        if (!formData.companyName) newErrors.push({ message: "A <b>Company Name</b> is required to create your account.", boldFields: ["Company Name"] });
        if (!formData.password) newErrors.push({ message: "A <b>Password</b> is required to create your account.", boldFields: ["Password"] });

        // Password security requirements
        if (formData.password.length < 8 || !/[A-Za-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
            newErrors.push({ message: "Please ensure your password is at least 8 characters long and contains a mix of letters and numbers", boldFields: [] });
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            setShowErrorModal(true);
            return;
        }

        try {
            const response = await fetch('https://api.foveaai.com/evaluate/web/user/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    company_name: formData.companyName,
                    password: formData.password,
                }),
            });

            if (response.ok) {
                const responseData = await response.json();
                
                // If registration returns a token, log the user in immediately
                if (responseData.token) {
                    setCookie('AuthorizationToken', responseData.token, 30);
                    
                    // Send data layer event to Google Tag Manager
                    try {
                        const dataLayerEvent = {
                            event: 'createdAccount',
                            userId: formData.email,
                        };
                    
                        TagManager.dataLayer({
                            dataLayer: dataLayerEvent
                        });
                    } catch (error) {
                        console.error("Error sending data layer event:", error);
                    }
                    
                    // Submit EULA acceptance if checkbox was checked
                    if (eulaAccepted) {
                        try {
                            await axios.post(
                                "https://api.foveaai.com/evaluate/web/eula/acceptances",
                                {
                                    eula_id: eulaID,
                                    method_of_acceptance: "web_registration_agree",
                                },
                                {
                                    headers: {
                                        "Content-Type": "application/json",
                                        Authorization: responseData.token,
                                    },
                                }
                            );
                        } catch (error) {
                            console.error("Failed to record EULA acceptance:", error);
                        }
                    }
                    
                    // Redirect to dashboard
                    navigate('/dashboard');
                } else {
                    // Fallback to old behavior if no token returned
                    setSuccessMessage('Your account has been created. An email has been sent to your email address. Please click the link in the email to verify your account.');
                }
            } else {
                const errorData = await response.json();
                setErrors([{ message: errorData.message || "Failed to register user — please try again later", boldFields: [] }]);
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error("Error registering user:", error);
            setErrors([{ message: "Error registering user", boldFields: [] }]);
            setShowErrorModal(true);
        }
    };

    const isPasswordValid = (password) => {
        return password.length >= 8 && /[A-Za-z]/.test(password) && /[0-9]/.test(password);
    };

    const showPasswordRequirements = formData.password.length > 0 && !isPasswordValid(formData.password);

    return (
        <div className="register-form-container">
            {successMessage ? (
                <div className="success-message">
                    <p>{successMessage}</p>
                </div>
            ) : (
                <Form className="register-form" onSubmit={handleSubmit}>
                    <Form.Group className="form-group">
                        <Form.Label htmlFor="email">Email Address</Form.Label>
                        <Form.Control type="email" className="form-control" id="email" name="email" value={formData.email} onChange={handleChange} required />
                    </Form.Group>
                    <Form.Group className="form-group">
                        <Form.Label htmlFor="companyName">Company Name</Form.Label>
                        <Form.Control type="text" className="form-control" id="companyName" name="companyName" value={formData.companyName} onChange={handleChange} required />
                    </Form.Group>
                    <Form.Group className="form-group">
                        <Form.Label htmlFor="password">Password</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                            <Button variant="outline-secondary" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                        </InputGroup>
                    </Form.Group>
                    {showPasswordRequirements && (
                        <div style={{ fontSize: '0.85em', color: '#dc3545', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                            Password must be at least 8 characters long and contain both letters and numbers.
                        </div>
                    )}
                    <Form.Group className="form-group">
                        <Form.Check
                            type="checkbox"
                            id="eula-checkbox"
                            checked={eulaAccepted}
                            onChange={(e) => setEulaAccepted(e.target.checked)}
                            label="I accept the EULA and privacy policy."
                        />
                        <div style={{ fontSize: '0.8em', marginTop: '0.25rem' }}>
                            <a href="#!" onClick={() => toggleModal('eula', 'End User License Agreement')}>
                                View EULA
                            </a>
                            {' | '}
                            <a href="#!" onClick={() => toggleModal('privacy', 'Privacy Policy')}>
                                View Privacy Policy
                            </a>
                        </div>
                    </Form.Group>
                    <Button type="submit" className="btn btn-primary" disabled={!eulaAccepted}>Create Account</Button>
                </Form>
            )}
            {showErrorModal && (
                <div className="error-modal">
                    <div className="error-modal-content">
                        <h3>Oops! Please check the following:</h3>
                        <ul>
                            {errors.map((error, index) => (
                                <li key={index}>
                                    {error.message.split(" ").map((word, i) => (
                                        error.boldFields.includes(word) ? <b key={i}>{word} </b> : `${word} `
                                    ))}
                                </li>
                            ))}
                        </ul>
                        <Button onClick={() => setShowErrorModal(false)} className="btn btn-danger">Close</Button>
                    </div>
                </div>
            )}
            <Modal show={showModal.open} onHide={() => toggleModal('', '')} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{showModal.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body dangerouslySetInnerHTML={{ __html: showModal.content }} />
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => toggleModal('', '')}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default RegisterForm;